/**
 * WebsiteOptimierungPage — Erster echte Website-Optimierungs-Workflow.
 *
 * Workflow:
 *   1. Seite lädt → GET /api/website-insights/latest (vorhandenes Markenprofil)
 *   2. URL-Eingabe — vorausgefüllt wenn Profil vorhanden
 *   3. "Analysieren" → POST /api/website-insights/optimize
 *   4. Strukturierte Empfehlungen in 3 Prioritätsstufen anzeigen
 *
 * Feature-Gate:
 *   Heute: website_insights = true für alle Pläne (kein Gate aktiv)
 *   Vorbereitet: website_optimization Add-on (pricing.js: status "coming_soon")
 *   → Wenn Add-on live: featureKey "website_optimization" hier einbauen
 *
 * Integrationspfade (siehe TOOLING.md):
 *   Browser Use CLI 2.0 → Live-Verifikation der Empfehlungen im Browser
 *   Stitch / Claude Code → actions[] als Change-Briefings für Website-Editing
 */

import { useState, useEffect } from "react";
import axios from "axios";
import { NavLink } from "react-router-dom";
import { toast } from "sonner";
import {
  Globe, Loader2, Search, Sparkles, AlertCircle, CheckCircle,
  RefreshCw, MessageSquare, Shield, Phone, Layout, BarChart2,
  Cpu, Zap, Target, TrendingUp, ArrowRight, ExternalLink, Clock,
  ChevronDown, ChevronUp,
} from "lucide-react";
import { formatRelativeWithAbsolute, isDistinctTime } from "../lib/relativeTime";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Zeitfelder robust lesen: `analyzed_at`/`saved_at` sind neu — ältere gespeicherte
// Profile kennen nur `updated_at`. Fehlt beides, liefern die Helfer null und die
// Zeile entfällt (lieber keine Angabe als „Invalid Date").
const analyzedAtOf = (d) => d?.analyzed_at || d?.updated_at || null;
const savedAtOf    = (d) => d?.saved_at    || d?.updated_at || null;

// ── Kategorie-Metadaten ───────────────────────────────────────────────────────

const CATEGORY_META = {
  messaging: {
    label: "Botschaft & Positionierung",
    icon:  MessageSquare,
    color: "text-violet-600",
    bg:    "bg-violet-50",
    border:"border-violet-200",
  },
  vertrauen: {
    label: "Vertrauen & Glaubwürdigkeit",
    icon:  Shield,
    color: "text-blue-600",
    bg:    "bg-blue-50",
    border:"border-blue-200",
  },
  cta: {
    label: "Kontakt & Handlungsaufruf",
    icon:  Phone,
    color: "text-green-600",
    bg:    "bg-green-50",
    border:"border-green-200",
  },
  struktur: {
    label: "Aufbau & Lesbarkeit",
    icon:  Layout,
    color: "text-amber-600",
    bg:    "bg-amber-50",
    border:"border-amber-200",
  },
  seo: {
    label: "Sichtbarkeit & SEO",
    icon:  Search,
    color: "text-cyan-600",
    bg:    "bg-cyan-50",
    border:"border-cyan-200",
  },
  technik: {
    label: "Technik & Performance",
    icon:  Cpu,
    color: "text-stone-600",
    bg:    "bg-stone-50",
    border:"border-stone-200",
  },
};

// ── Prioritäts-Metadaten ──────────────────────────────────────────────────────

const PRIORITY_META = {
  quick_win: {
    label:  "Quick Win",
    badge:  "bg-green-100 text-green-700",
    icon:   Zap,
    heading:"Sofort umsetzen",
    headingBg: "bg-green-50 border-green-200",
    headingText: "text-green-800",
    headingIcon: "text-green-500",
    empty:  "Keine schnell umsetzbaren Maßnahmen identifiziert.",
  },
  conversion: {
    label:  "Conversion-Hebel",
    badge:  "bg-amber-100 text-amber-700",
    icon:   Target,
    heading:"Conversion stärken",
    headingBg: "bg-amber-50 border-amber-200",
    headingText: "text-amber-800",
    headingIcon: "text-amber-500",
    empty:  "Keine Conversion-Hebel identifiziert.",
  },
  strategic: {
    label:  "Strategisch",
    badge:  "bg-blue-100 text-blue-700",
    icon:   TrendingUp,
    heading:"Mittelfristig angehen",
    headingBg: "bg-blue-50 border-blue-200",
    headingText: "text-blue-800",
    headingIcon: "text-blue-500",
    empty:  "Keine strategischen Maßnahmen identifiziert.",
  },
};

// ── Einzelne Empfehlungskarte ─────────────────────────────────────────────────

const RecommendationCard = ({ rec }) => {
  const [expanded, setExpanded] = useState(true);
  const cat  = CATEGORY_META[rec.category]  || CATEGORY_META.messaging;
  const prio = PRIORITY_META[rec.priority]  || PRIORITY_META.strategic;
  const CatIcon  = cat.icon;
  const PrioIcon = prio.icon;

  return (
    <div className={`rounded-xl border ${cat.border} bg-white overflow-hidden`}>
      {/* Card Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-start gap-3 p-4 text-left hover:bg-stone-50 transition-colors"
      >
        {/* Category icon */}
        <div className={`w-8 h-8 rounded-lg ${cat.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
          <CatIcon size={15} className={cat.color} />
        </div>

        {/* Title + badges */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="text-xs font-medium text-stone-400">{cat.label}</span>
            <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${prio.badge}`}>
              <PrioIcon size={10} />
              {prio.label}
            </span>
          </div>
          <p className="font-semibold text-stone-800 text-sm leading-snug">{rec.title}</p>
        </div>

        {/* Expand toggle */}
        <div className="flex-shrink-0 text-stone-400 mt-0.5">
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </button>

      {/* Card Body */}
      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-stone-100">
          <p className="text-sm text-stone-600 leading-relaxed pt-3">{rec.description}</p>

          {rec.actions && rec.actions.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-stone-400 uppercase tracking-wide">
                Konkrete Maßnahmen
              </p>
              {rec.actions.map((action, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <div className={`w-5 h-5 rounded-full ${cat.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                    <span className={`text-xs font-bold ${cat.color}`}>{i + 1}</span>
                  </div>
                  <p className="text-sm text-stone-700 leading-snug">{action}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Prioritäts-Abschnitt ──────────────────────────────────────────────────────

const PrioritySection = ({ priorityKey, recommendations }) => {
  const meta = PRIORITY_META[priorityKey];
  const HeadIcon = meta.icon;

  return (
    <div>
      {/* Section Header */}
      <div className={`flex items-center gap-2.5 px-4 py-2.5 rounded-xl border ${meta.headingBg} mb-3`}>
        <HeadIcon size={16} className={meta.headingIcon} />
        <h3 className={`font-semibold text-sm ${meta.headingText}`}>{meta.heading}</h3>
        <span className={`ml-auto text-xs font-medium px-2 py-0.5 rounded-full ${meta.badge}`}>
          {recommendations.length}
        </span>
      </div>

      {/* Cards */}
      {recommendations.length === 0 ? (
        <p className="text-sm text-stone-400 italic px-2">{meta.empty}</p>
      ) : (
        <div className="space-y-3">
          {recommendations.map((rec, i) => (
            <RecommendationCard key={i} rec={rec} />
          ))}
        </div>
      )}
    </div>
  );
};

// ── Ergebnis-Anzeige ──────────────────────────────────────────────────────────

const OptimizationResults = ({ result, storedProfile }) => {
  const byPriority = {
    quick_win:  result.recommendations.filter(r => r.priority === "quick_win"),
    conversion: result.recommendations.filter(r => r.priority === "conversion"),
    strategic:  result.recommendations.filter(r => r.priority === "strategic"),
  };

  // Bei „used_cached" wurde exakt das Profil wiederverwendet, das beim Laden der
  // Seite über /latest kam — dessen Zeit ist der ehrliche Rückfall, falls die
  // Optimize-Antwort (noch) keine Zeitfelder mitliefert.
  const cachedAt   = analyzedAtOf(result) || analyzedAtOf(storedProfile);
  const cachedTime = formatRelativeWithAbsolute(cachedAt);

  return (
    <div className="space-y-6">
      {/* Gesamt-Fazit */}
      {result.summary && (
        <div className="rounded-xl border border-orange-200 bg-orange-50 p-4 flex gap-3">
          <Sparkles size={18} className="text-fox-fire flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-semibold text-orange-500 uppercase tracking-wide mb-1">
              Gesamt-Fazit
            </p>
            <p className="text-sm text-orange-800 leading-relaxed">{result.summary}</p>
          </div>
        </div>
      )}

      {/* Meta-Info */}
      <div className="flex flex-wrap items-center gap-3 text-xs text-stone-400">
        {result.brand_name && (
          <span className="flex items-center gap-1">
            <Globe size={12} />
            {result.brand_name}
          </span>
        )}
        {result.url && (
          <a
            href={result.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 hover:text-stone-600 transition-colors"
          >
            <ExternalLink size={11} />
            {result.url.replace(/^https?:\/\//, "").replace(/\/$/, "")}
          </a>
        )}
        {result.used_cached && (
          <span className="flex items-center gap-1 text-blue-400" data-testid="used-cached-note">
            <Clock size={11} />
            {cachedTime
              ? `Auf Basis gespeichertem Profil — zuletzt analysiert: ${cachedTime}`
              : "Auf Basis gespeichertem Profil"}
          </span>
        )}
        <span>{result.recommendations.length} Empfehlungen</span>
      </div>

      {/* Priorisierte Abschnitte */}
      {["quick_win", "conversion", "strategic"].map(pKey => (
        byPriority[pKey].length > 0 && (
          <PrioritySection
            key={pKey}
            priorityKey={pKey}
            recommendations={byPriority[pKey]}
          />
        )
      ))}
    </div>
  );
};

// ── Vorhandenes Markenprofil — kurze Zusammenfassung ─────────────────────────

const BrandContextSummary = ({ profile }) => {
  if (!profile?.has_profile) return null;
  const snap = profile.brand_snapshot;
  if (!snap) return null;

  const analyzedAt   = analyzedAtOf(profile);
  const savedAt      = savedAtOf(profile);
  const analyzedTime = formatRelativeWithAbsolute(analyzedAt);
  const savedTime    = formatRelativeWithAbsolute(savedAt);
  // „Zuletzt gespeichert" nur, wenn es sich wirklich von der Analyse unterscheidet —
  // zwei identische Zeitangaben nebeneinander wären Schein-Genauigkeit.
  const showSaved    = !!savedTime && isDistinctTime(analyzedAt, savedAt);

  return (
    <div className="rounded-xl border border-stone-200 bg-stone-50 p-4 flex items-start gap-3">
      <CheckCircle size={16} className="text-green-500 flex-shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-stone-500 mb-1">
          Vorhandenes Markenprofil wird genutzt
        </p>
        <p className="text-sm text-stone-700 font-medium truncate">{snap.brand_name}</p>
        {snap.target_audience && (
          <p className="text-xs text-stone-500 mt-0.5 line-clamp-1">{snap.target_audience}</p>
        )}
        {analyzedTime && (
          <p className="text-xs text-stone-400 mt-1.5 flex items-center gap-1" data-testid="profile-analyzed-at">
            <Clock size={11} className="flex-shrink-0" />
            Zuletzt analysiert: {analyzedTime}
          </p>
        )}
        {showSaved && (
          <p className="text-xs text-stone-400 mt-0.5" data-testid="profile-saved-at">
            Zuletzt gespeichert: {savedTime}
          </p>
        )}
      </div>
      <NavLink
        to="/settings#website-analyse"
        className="text-xs text-fox-fire hover:underline flex-shrink-0 flex items-center gap-1"
      >
        Aktualisieren
        <ArrowRight size={11} />
      </NavLink>
    </div>
  );
};

// ── Hauptkomponente ───────────────────────────────────────────────────────────

const WebsiteOptimierungPage = () => {
  const [url, setUrl]               = useState("");
  const [profile, setProfile]       = useState(null);   // LatestInsightResponse
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [analyzing, setAnalyzing]   = useState(false);
  const [result, setResult]         = useState(null);   // OptimizationResponse
  const [error, setError]           = useState(null);

  // ── Vorhandenes Profil laden ────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/website-insights/latest`);
        if (res.data?.has_profile) {
          setProfile(res.data);
          setUrl(res.data.raw_url || "");
        }
      } catch {
        /* kein Profil — kein Fehler */
      } finally {
        setLoadingProfile(false);
      }
    };
    load();
  }, []);

  // ── Analyse starten ─────────────────────────────────────────────────────
  const handleAnalyze = async () => {
    const trimmed = url.trim();
    setError(null);
    setResult(null);
    setAnalyzing(true);

    try {
      const res = await axios.post(`${API}/website-insights/optimize`, {
        url: trimmed || undefined,
      });
      const data = res.data;

      if (data.status === "ok") {
        setResult(data);
        // Zeitstempel im angezeigten Profil nachziehen — sonst behauptet die
        // Zusammenfassung nach einer frischen Analyse weiter den alten Stand.
        if (data.analyzed_at || data.saved_at || data.updated_at) {
          setProfile((prev) =>
            prev
              ? {
                  ...prev,
                  analyzed_at: data.analyzed_at ?? prev.analyzed_at,
                  saved_at:    data.saved_at    ?? prev.saved_at,
                  updated_at:  data.updated_at  ?? prev.updated_at,
                }
              : prev,
          );
        }
        toast.success("Analyse abgeschlossen", {
          description: `${data.recommendations?.length || 0} Empfehlungen erstellt`,
        });
      } else {
        setError(
          data.message ||
          "Analyse fehlgeschlagen. Bitte überprüfe die URL und versuche es erneut.",
        );
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 429) {
        setError(detail || "Bitte warte kurz, bevor du die gleiche URL erneut analysierst.");
      } else if (err.response?.status === 422) {
        setError(detail || "Keine gültige URL. Bitte prüfe deine Eingabe.");
      } else {
        setError(
          "Die Website konnte nicht geladen werden. " +
          "Bitte prüfe die URL oder versuche es später erneut.",
        );
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !analyzing) handleAnalyze();
  };

  const hasUrl = url.trim().length > 0;

  return (
    <div
      className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6"
      data-testid="website-optimierung-page"
    >
      <div className="max-w-3xl mx-auto space-y-6">

        {/* ── Page Header ───────────────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row sm:items-start gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                <Globe size={20} className="text-fox-fire" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-stone-900">Website-Optimierung</h1>
                <p className="text-stone-500 text-sm">
                  Strukturierte Analyse — die wichtigsten Hebel auf einen Blick.
                </p>
              </div>
            </div>
          </div>

          {/* Add-on Hinweis — vorbereitet für website_optimization Add-on */}
          <div className="flex-shrink-0">
            <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-orange-50 border border-orange-200 text-orange-600">
              <Sparkles size={11} />
              Website-Optimierung Add-on
            </span>
          </div>
        </div>

        {/* ── URL-Eingabe ────────────────────────────────────────────────── */}
        <div className="bg-white rounded-xl border border-stone-200 p-5 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-stone-700 mb-2">
              Website-URL
            </label>
            <div className="flex gap-2">
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="https://deine-website.de"
                disabled={analyzing}
                className="flex-1 px-3 py-2.5 rounded-lg border border-stone-200 text-sm
                           focus:outline-none focus:ring-2 focus:ring-orange-300
                           disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="url-input"
              />
              <button
                onClick={handleAnalyze}
                disabled={analyzing}
                className="flex items-center gap-2 px-5 py-2.5 bg-fox-fire text-white
                           rounded-lg text-sm font-semibold hover:bg-orange-600
                           transition-colors disabled:opacity-40 disabled:cursor-not-allowed
                           flex-shrink-0"
                data-testid="analyze-btn"
              >
                {analyzing ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Search size={16} />
                )}
                {analyzing ? "Wird analysiert…" : "Analysieren"}
              </button>
            </div>

            {/* Hinweis unter Eingabe */}
            <p className="text-xs text-stone-400 mt-2 leading-relaxed">
              Es werden nur öffentliche Inhalte gelesen — keine Anmeldung, kein Zugriff auf
              geschützte Bereiche. Die Analyse dauert ca. 15–45 Sekunden.
            </p>
          </div>

          {/* Vorhandenes Markenprofil */}
          {!loadingProfile && (
            <BrandContextSummary profile={profile} />
          )}
        </div>

        {/* ── Lade-Zustand ──────────────────────────────────────────────── */}
        {analyzing && (
          <div
            className="bg-white rounded-xl border border-stone-200 p-8 text-center"
            data-testid="analyzing-state"
          >
            <div className="w-14 h-14 bg-orange-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <Loader2 size={28} className="animate-spin text-fox-fire" />
            </div>
            <h3 className="font-semibold text-stone-800 mb-1">Website wird analysiert</h3>
            <p className="text-sm text-stone-500 max-w-xs mx-auto leading-relaxed">
              Seiteninhalte werden gelesen, Markenprofil ausgewertet und
              Optimierungsempfehlungen erstellt. Das kann einen Moment dauern.
            </p>
          </div>
        )}

        {/* ── Fehleranzeige ─────────────────────────────────────────────── */}
        {error && !analyzing && (
          <div
            className="rounded-xl border border-red-200 bg-red-50 p-4 flex items-start gap-3"
            role="alert"
            data-testid="error-state"
          >
            <AlertCircle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-red-700 leading-relaxed">{error}</p>
              <button
                onClick={handleAnalyze}
                className="mt-2 text-xs font-medium text-red-600 hover:underline flex items-center gap-1"
              >
                <RefreshCw size={11} />
                Erneut versuchen
              </button>
            </div>
          </div>
        )}

        {/* ── Leer-Zustand (keine URL, noch kein Ergebnis) ─────────────── */}
        {!loadingProfile && !profile && !result && !analyzing && !error && (
          <div className="bg-white rounded-xl border border-stone-200 p-8 text-center">
            <div className="w-14 h-14 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Globe size={28} className="text-stone-300" />
            </div>
            <h3 className="font-semibold text-stone-700 mb-2">
              Noch keine Analyse vorhanden
            </h3>
            <p className="text-sm text-stone-500 max-w-xs mx-auto leading-relaxed mb-4">
              Gib deine Website-URL ein und klicke auf „Analysieren" — du bekommst
              strukturierte Empfehlungen in weniger als einer Minute.
            </p>
            <NavLink
              to="/settings"
              className="text-xs text-fox-fire hover:underline"
            >
              Oder zuerst Markenprofil in den Einstellungen anlegen →
            </NavLink>
          </div>
        )}

        {/* ── Ergebnisse ────────────────────────────────────────────────── */}
        {result && !analyzing && (
          <div data-testid="optimization-results">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-stone-800 text-lg">
                Optimierungsempfehlungen
              </h2>
              <button
                onClick={handleAnalyze}
                className="flex items-center gap-1.5 text-xs text-stone-500 hover:text-stone-700
                           border border-stone-200 rounded-lg px-3 py-1.5 transition-colors"
              >
                <RefreshCw size={12} />
                Neu analysieren
              </button>
            </div>
            <OptimizationResults result={result} storedProfile={profile} />
          </div>
        )}

        {/* ── Integrations-Hinweis (Browser Use / Stitch) ───────────────── */}
        {/*
          Integrationspfade — vorbereitet, noch nicht aktiv (siehe TOOLING.md):
          Browser Use CLI 2.0:
            Sobald aktiv → Live-Verifikation jeder Empfehlung im echten Browser möglich
            Einstiegspunkt: browser-use/workflows/website-audit.yaml
            Konkret: CTA-Sichtbarkeit prüfen, LCP messen, Mobile-Layout validieren

          Stitch / Claude Code:
            Einstiegspunkt: RecommendationCard.actions[] → strukturiertes Change-Briefing
            Konkret: Jede "actions[]"-Zeile wird zur Claude-Code-Anweisung für direktes Edit
        */}
        {result && (
          <div className="rounded-xl border border-stone-200 bg-stone-50 p-4 flex items-start gap-3">
            <Sparkles size={15} className="text-stone-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-stone-500 mb-1">
                Website-Optimierungs-Add-on (demnächst)
              </p>
              <p className="text-xs text-stone-400 leading-relaxed">
                In einer kommenden Version: automatisierte Live-Checks im Browser,
                direkte Umsetzungshilfe und mehr — als buchbares Add-on.{" "}
                <NavLink to="/pricing" className="text-fox-fire hover:underline">
                  Preise & Pakete →
                </NavLink>
              </p>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default WebsiteOptimierungPage;
